import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { day, num } from "../format";
import type { ExecutionRow, Tradebook } from "../types";

/**
 * The broker fills behind imported trades, editable.
 *
 * Imported trades are derived, so correcting one means correcting the fill it
 * came from. Edits are stored as an overlay rather than overwriting the row —
 * the broker's original values stay on record and any correction can be
 * reverted, which matters when the "wrong" figure turns out to be right.
 */
export function RawFills({ book }: { book: Tradebook }) {
  const [rows, setRows] = useState<ExecutionRow[]>([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<ExecutionRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.executions(book.id, search || undefined));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [book.id, search]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="card">
      <div className="section-title" style={{ marginTop: 0 }}>
        Broker fills · {book.name}
      </div>
      <p className="hint" style={{ marginTop: 0 }}>
        The raw imported data. Trades are rebuilt from these, so this is where an imported
        trade gets corrected. Every edit is kept as an overlay — the original import is never
        overwritten and any change can be reverted.
      </p>

      {error && <div className="banner error">{error}</div>}

      <div className="row" style={{ marginBottom: 12 }}>
        <input
          value={search}
          placeholder="Filter by symbol…"
          style={{ maxWidth: 260 }}
          onChange={(e) => setSearch(e.target.value.toUpperCase())}
        />
        <span className="hint">
          {loading ? "Loading…" : `${rows.length} fill${rows.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {rows.length === 0 && !loading ? (
        <div className="hint">
          No imported fills in this book. Manual trades are edited from the Manage tab.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="legs">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Date</th>
                <th>Type</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Exchange</th>
                <th>Broker ID</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.symbol}</strong>
                    {row.edited && (
                      <span
                        className="badge muted"
                        title={`Broker's original: ${JSON.stringify(row.original)}`}
                      >
                        edited
                      </span>
                    )}
                  </td>
                  <td className="nowrap">{day(row.trade_date)}</td>
                  <td>
                    <span className={`badge ${row.trade_type === "buy" ? "open" : "muted"}`}>
                      {row.trade_type}
                    </span>
                  </td>
                  <td>{row.quantity}</td>
                  <td>{num(row.price)}</td>
                  <td className="muted">{row.exchange ?? "—"}</td>
                  <td className="muted">{row.trade_id}</td>
                  <td>
                    <div className="row-actions">
                      <button className="ghost" title="Edit" onClick={() => setEditing(row)}>
                        ✎
                      </button>
                      {row.edited && (
                        <button
                          className="ghost"
                          title="Revert to the broker's original"
                          onClick={() => act(() => api.revertExecution(row.id))}
                        >
                          ↺
                        </button>
                      )}
                      <button
                        className="ghost danger"
                        title="Delete this fill"
                        onClick={() => {
                          if (
                            confirm(
                              `Delete this ${row.trade_type} of ${row.quantity} ${row.symbol}? ` +
                                `Trades will be rebuilt. Re-importing the file brings it back.`,
                            )
                          ) {
                            act(() => api.deleteExecution(row.id));
                          }
                        }}
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditFill
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function EditFill({
  row,
  onClose,
  onSaved,
}: {
  row: ExecutionRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    symbol: row.symbol,
    trade_date: row.trade_date,
    trade_type: row.trade_type,
    quantity: String(row.quantity),
    price: String(row.price),
    exchange: row.exchange ?? "NSE",
    reason: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.editExecution(row.id, {
        symbol: form.symbol,
        trade_date: form.trade_date,
        trade_type: form.trade_type,
        quantity: Number(form.quantity),
        price: Number(form.price),
        exchange: form.exchange,
        reason: form.reason || null,
      });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <strong>Correct broker fill</strong>
          <button className="ghost right" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          {error && <div className="banner error">{error}</div>}
          <div className="hint" style={{ marginBottom: 12 }}>
            Broker trade ID {row.trade_id}. The original values are kept, so this can be
            reverted at any time.
          </div>

          <div className="grid cols-3">
            <div>
              <label>Symbol</label>
              <input
                value={form.symbol}
                onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <label>Date</label>
              <input
                type="date"
                value={form.trade_date}
                onChange={(e) => setForm({ ...form, trade_date: e.target.value })}
              />
            </div>
            <div>
              <label>Type</label>
              <select
                value={form.trade_type}
                onChange={(e) =>
                  setForm({ ...form, trade_type: e.target.value as "buy" | "sell" })
                }
              >
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
              </select>
            </div>
          </div>

          <div className="grid cols-3" style={{ marginTop: 12 }}>
            <div>
              <label>Quantity</label>
              <input
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              />
            </div>
            <div>
              <label>Price</label>
              <input
                type="number"
                step="0.05"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>
            <div>
              <label>Exchange</label>
              <select
                value={form.exchange}
                onChange={(e) => setForm({ ...form, exchange: e.target.value })}
              >
                <option value="NSE">NSE</option>
                <option value="BSE">BSE</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label>Why? (optional, kept with the correction)</label>
            <input
              value={form.reason}
              placeholder="e.g. broker reported the wrong quantity"
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </div>
        </div>
        <div className="modal-foot">
          <button className="ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="primary" disabled={busy} onClick={save}>
            {busy ? "Saving…" : "Save and rebuild"}
          </button>
        </div>
      </div>
    </div>
  );
}
